import './SCBilling.scss';
import FlexibleContentTile from '../../../Shared/Tiles/FlexibleContentTile';
import RecentActivitiesNoData, { GetNoDataTile, INoDataTileMessage } from '../../../Shared/Tiles/NoDataTile';
import LoadingScreen from '../LoadingScreen';
import NoAccessScreen from '../NoAccessScreen';
import { useEffect, useState } from 'react';
import { Table, DataTable, TableCell, TableRow, TableHeader, TableBody } from '@lmig/lmds-react-table';
import { Pagination } from '@lmig/lmds-react-pagination';
import { Link } from '@lmig/lmds-react-link';
import { toTitleCase } from 'Utilities/StringFormatter';
import { SitecoreFetch } from 'Api/api/SitecoreApi';
import { useUserData } from 'Foundation/User/UserContext';
import { HttpStatusCodes, IServerResponse } from 'Utilities/ClientResponseUtility';
import { useSessionStorage } from 'Utilities/UseStorage';
import { GetLOBIconFromPolicy, GetLOBNameFromPolicy } from '../LOBMapper';
import { getRecentActivitiesLink, isProd } from 'Foundation/Configurations/GlobalConfiguration';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { getMajescoBillingURLByPolicy } from 'Utilities/URLHelper';
import { OverflowTooltip, Tooltip } from 'Foundation/Common/Tooltip';

interface IBillingDocument {
  ActivityDate: string;
  ActivityType: string;
  AgencyCode: string;
  AgencyName: string;
  InsuredName: string;
  MinimumDue: string;
  PolicyNumber: string;
}

enum ActivityTypes {
  pendingCancellation = 'Pend Canc',
  reinstatement = 'Reinstate',
  none = 'none',
}

enum DisplayActivityTypes {
  pendingCancellation = 'Pend Canc',
  reinstatement = 'Reinstatement',
  none = 'none',
}

const ActivityTypeConversion: Record<ActivityTypes, DisplayActivityTypes> = {
  [ActivityTypes.pendingCancellation]: DisplayActivityTypes.pendingCancellation,
  [ActivityTypes.reinstatement]: DisplayActivityTypes.reinstatement,
  [ActivityTypes.none]: DisplayActivityTypes.none,
};

enum SortDirection {
  ascending = 'ascending',
  descending = 'descending',
  none = 'none',
}

enum SCBillingTableSortableKeys {
  activity = 'activity',
  activityDate = 'activityDate',
  insuredName = 'insuredName',
  minimumDue = 'minimumDue',
  policyNumber = 'policyNumber',
}

const keyToProp: Record<SCBillingTableSortableKeys, keyof IBillingDocument> = {
  [SCBillingTableSortableKeys.activity]: 'ActivityType',
  [SCBillingTableSortableKeys.activityDate]: 'ActivityDate',
  [SCBillingTableSortableKeys.insuredName]: 'InsuredName',
  [SCBillingTableSortableKeys.minimumDue]: 'MinimumDue',
  [SCBillingTableSortableKeys.policyNumber]: 'PolicyNumber',
};

interface SortDirections {
  [SCBillingTableSortableKeys.activity]: SortDirection;
  [SCBillingTableSortableKeys.activityDate]: SortDirection;
  [SCBillingTableSortableKeys.insuredName]: SortDirection;
  [SCBillingTableSortableKeys.minimumDue]: SortDirection;
  [SCBillingTableSortableKeys.policyNumber]: SortDirection;
}

const GetEmptySortDirections = {
  [SCBillingTableSortableKeys.activity]: SortDirection.none,
  [SCBillingTableSortableKeys.activityDate]: SortDirection.none,
  [SCBillingTableSortableKeys.insuredName]: SortDirection.none,
  [SCBillingTableSortableKeys.minimumDue]: SortDirection.none,
  [SCBillingTableSortableKeys.policyNumber]: SortDirection.none,
};

interface IBillingResponse {
  BillingData: IBillingDocument[];
  ErrorMessage: string;
  Status: string;
}

export default function Billing(props: { activeTab: number }) {
  const { userData } = useUserData();
  const resultsPerPage = 5;
  const maxResults = 49;
  const expirationMinutes = 12 * 60;
  const [rerender, setRerender] = useState(0);
  const [page, setPage] = useState(0);
  const [sending, setSending] = useState<string>('');
  const [billing, setBilling] = useSessionStorage<{
    activities?: IBillingDocument[] | null;
    isLoaded: boolean;
    noAccess: boolean;
  }>(
    'sc-billing-session:' + userData?.selectedStatCodeData?.SubStat,
    { isLoaded: false, noAccess: false },
    expirationMinutes,
  );
  const [sortedBilling, setSortedBilling] = useState<IBillingDocument[]>([]);
  const [currentSortKey, setCurrentSortKey] = useState<SCBillingTableSortableKeys>(
    SCBillingTableSortableKeys.insuredName,
  );
  const [sortDirections, setSortDirections] = useState<SortDirections>({
    ...GetEmptySortDirections,
    [SCBillingTableSortableKeys.insuredName]: SortDirection.descending,
  });

  const [noDataTile, setNoDataTile] = useState<INoDataTileMessage>({
    leftText: '<div>Loading...</div>',
    rightText: '<div>Loading...</div>',
  });

  const billingTableSorter = (key: SCBillingTableSortableKeys) => {
    setSortDirections((prev) => ({
      ...GetEmptySortDirections,
      [key]: prev[key] === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending,
    }));
    setCurrentSortKey(key);
  };

  const areMoreRecordsThanAvailable = () => {
    return billing?.activities && sortedBilling.length < billing.activities.length && page === 9;
  };

  const compareBillingDocuments = (a: IBillingDocument, b: IBillingDocument) => {
    try {
      if (currentSortKey === SCBillingTableSortableKeys.minimumDue) {
        const aValue = parseFloat(a.MinimumDue.replace(/[^0-9.-]+/g, ''));
        const bValue = parseFloat(b.MinimumDue.replace(/[^0-9.-]+/g, ''));
        return sortDirections[currentSortKey] === SortDirection.ascending ? aValue - bValue : bValue - aValue;
      } else if (currentSortKey === SCBillingTableSortableKeys.activityDate) {
        const aDate = new Date(a.ActivityDate);
        const bDate = new Date(b.ActivityDate);
        return sortDirections[currentSortKey] === SortDirection.ascending
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      } else {
        return sortDirections[currentSortKey] === SortDirection.ascending
          ? a[keyToProp[currentSortKey]].toLowerCase().localeCompare(b[keyToProp[currentSortKey]].toLowerCase())
          : b[keyToProp[currentSortKey]].toLowerCase().localeCompare(a[keyToProp[currentSortKey]].toLowerCase());
      }
    } catch (error) {
      console.error('Error sorting:', error);
      return 0; // Default to no sorting if an error occurs
    }
  };

  const sortTable = (unsorted: IBillingDocument[]) => {
    const sorted = unsorted.sort((a: IBillingDocument, b: IBillingDocument) => {
      return compareBillingDocuments(a, b);
    });
    return sorted.slice(0, maxResults);
  };

  const LMDSPagination = () => {
    const itemCount = sortedBilling?.length;
    const changeHandler = (e: any) => {
      setPage(e.page);
    };

    return (
      (itemCount > resultsPerPage && (
        <Pagination
          id="billing-pagination"
          className="recent-activities-pagination"
          withWords={false}
          itemCount={itemCount}
          onChange={changeHandler}
          showItemsPerPage={false}
          page={page}
          itemsPerPage={resultsPerPage}
        />
      )) || <></>
    );
  };

  const isValidBillingData = (data: IBillingDocument) => {
    return (
      !!data &&
      typeof data.PolicyNumber === 'string' &&
      data.PolicyNumber.trim() !== '' &&
      typeof data.InsuredName === 'string' &&
      data.InsuredName.trim() !== '' &&
      typeof data.MinimumDue === 'string' &&
      data.MinimumDue.trim() !== '' &&
      typeof data.ActivityDate === 'string' &&
      data.ActivityDate.trim() !== '' &&
      typeof data.ActivityType === 'string' &&
      data.ActivityType.trim() !== ''
    );
  };

  const redirectToBilling = async (policyNumber: string, agencyCode: string) => {
    if (!userData || sending) return;
    setSending(policyNumber);
    try {
      let policyURL: string = await getMajescoBillingURLByPolicy(policyNumber, agencyCode);
      setSending('');
      if (policyURL) global.open(policyURL, '_blank')?.focus();
    } catch (e) {
      setSending('');
    }
  };

  const filterDates = (activities: IBillingDocument[]) => {
    try {
      let dateOffset = isProd() ? -3 : -30;
      let oldestDate = new Date();
      oldestDate.setDate(oldestDate.getDate() + dateOffset);
      return activities.filter((res) => new Date(res.ActivityDate) > oldestDate);
    } catch (Exception) {}
    return activities;
  };

  useEffect(() => {
    const fetchData = async () => {
      setBilling({ data: { isLoaded: false, noAccess: false } });
      let noAccessResponse = false;
      let response = (await SitecoreFetch(`/api/sitecore/BillingAsync/GetBillingRecentActivities`)) as IServerResponse;
      let result: IBillingDocument[] | null = null;
      if (response.Status.Code === HttpStatusCodes.OK) {
        let billingResponse: IBillingResponse | null = response.Data as IBillingResponse;
        result = filterDates(billingResponse?.BillingData);
      } else if (response.Status.Code === HttpStatusCodes.Forbidden) {
        noAccessResponse = true;
      }
      setBilling({ data: { activities: result, isLoaded: true, noAccess: noAccessResponse } });
    };

    try {
      if (userData?.isAgent() || userData?.isMockingAgent) {
        if (!billing?.isLoaded) fetchData();
      } else {
        setBilling({ data: { activities: null, isLoaded: true, noAccess: false } });
      }
    } catch (error) {
      console.error(error);
    }
  }, [userData?.selectedStatCodeData?.SubStat]);

  useEffect(() => {
    if (billing?.isLoaded) {
      if (billing.activities && billing.activities?.length > 0) {
        setRerender((r) => r + 1);
        setSortedBilling(sortTable(billing.activities));
      } else {
        GetNoDataTile('billing', setNoDataTile);
      }
    }
  }, [billing?.isLoaded, billing?.activities]);

  useEffect(() => {
    if (billing?.activities) {
      setSortedBilling(sortTable(billing.activities));
      setPage(0);
    }
  }, [sortDirections]);

  return !billing?.isLoaded ? (
    <LoadingScreen />
  ) : billing?.noAccess ? (
    <NoAccessScreen tabName="billing" />
  ) : billing?.activities && billing.activities.length !== 0 ? (
    <FlexibleContentTile
      data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
      tileClass="billing-tile"
      children={[
        <>
          <div id="sc-billing-table-container" className="recent-activities-table-container">
            <div key={props.activeTab} className="sc-billing-tab-content tab-content">
              <div className="design-system-data-table-container">
                <div className="design-system-data-table">
                  <DataTable>
                    <Table id="sc-billing-table" spacing="micro">
                      <TableHeader id="billing-table-header">
                        <TableRow>
                          <TableCell
                            className="billing-lob"
                            type="colHead"
                            isSortable
                            sortDirection={sortDirections.policyNumber}
                            onClick={() => billingTableSorter(SCBillingTableSortableKeys.policyNumber)}
                          >
                            LOB
                          </TableCell>
                          <TableCell
                            className="billing-insured-name"
                            type="colHead"
                            isSortable
                            sortDirection={sortDirections.insuredName}
                            onClick={() => billingTableSorter(SCBillingTableSortableKeys.insuredName)}
                          >
                            Insured name
                          </TableCell>
                          <TableCell
                            className="billing-minimum-due"
                            type="colHead"
                            isSortable
                            sortDirection={sortDirections.minimumDue}
                            onClick={() => billingTableSorter(SCBillingTableSortableKeys.minimumDue)}
                          >
                            Minimum due
                          </TableCell>
                          <TableCell
                            className="billing-activity-date"
                            type="colHead"
                            isSortable
                            sortDirection={sortDirections.activityDate}
                            onClick={() => billingTableSorter(SCBillingTableSortableKeys.activityDate)}
                          >
                            Activity date
                          </TableCell>
                          <TableCell
                            className="billing-activity-type"
                            type="colHead"
                            isSortable
                            sortDirection={sortDirections.activity}
                            onClick={() => billingTableSorter(SCBillingTableSortableKeys.activity)}
                          >
                            Activity
                          </TableCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody id="billing-table-body" key={rerender}>
                        {sortedBilling?.length ? (
                          sortedBilling
                            .slice(
                              page * resultsPerPage,
                              Math.min(page * resultsPerPage + resultsPerPage, sortedBilling?.length),
                            )
                            .map(
                              (data: IBillingDocument, rowIndex: any) =>
                                isValidBillingData(data) && (
                                  <TableRow>
                                    <TableCell key={`${rowIndex}-billing-lob`} className="billing-lob-cell">
                                      <Tooltip text={GetLOBNameFromPolicy(data.PolicyNumber)}>
                                        <div>{GetLOBIconFromPolicy(data.PolicyNumber)}</div>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell
                                      key={`${rowIndex}-billing-insured-name`}
                                      className="billing-insured-name-cell"
                                    >
                                      <Link onClick={() => redirectToBilling(data.PolicyNumber, data.AgencyCode)}>
                                        {sending && sending === data.PolicyNumber ? (
                                          <CircularProgressSpinner
                                            size="16"
                                            aria-label="Loading billing application..."
                                          />
                                        ) : (
                                          <OverflowTooltip text={toTitleCase(data.InsuredName) ?? ''}>
                                            <div>{toTitleCase(data.InsuredName)}</div>
                                          </OverflowTooltip>
                                        )}
                                      </Link>
                                    </TableCell>
                                    <TableCell
                                      key={`${rowIndex}-billing-minimum-due`}
                                      className="billing-minimum-due-cell"
                                    >
                                      {data.MinimumDue}
                                    </TableCell>
                                    <TableCell
                                      key={`${rowIndex}-billing-activity-date`}
                                      className="billing-activity-date-cell"
                                    >
                                      <div>{data.ActivityDate}</div>
                                    </TableCell>
                                    <TableCell
                                      key={`${rowIndex}-billing-activity-type`}
                                      className="billing-activity-type-cell"
                                    >
                                      <div>
                                        {ActivityTypeConversion[data.ActivityType as ActivityTypes] ??
                                          data.ActivityType}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ),
                            )
                        ) : (
                          <></>
                        )}
                      </TableBody>
                    </Table>
                  </DataTable>

                  {areMoreRecordsThanAvailable() && (
                    <div className="view-all-recent-activities">
                      <Link
                        target="_blank"
                        href={getRecentActivitiesLink()}
                        variant="standalone"
                        className="portal-tile-v2-header-link"
                      >
                        View all recent activities
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <LMDSPagination />
          </div>
        </>,
      ]}
    ></FlexibleContentTile>
  ) : (
    <RecentActivitiesNoData
      messageLeft={noDataTile?.leftText}
      messageRight={noDataTile?.rightText}
    ></RecentActivitiesNoData>
  );
}
